import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputLabel,
    Stack,
    Typography,
} from '@mui/material';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    useAddCreditHoldMutation,
    useGetAllGuestsQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import React, { useEffect, useState } from 'react';
import { Close, CreditCardOff } from '@mui/icons-material';
import { useForm, FormProvider } from 'react-hook-form';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import FormAutocomplete from '../../shared/FormAutocomplete';
import toast from '../../shared/Toast';
import { ModalProps } from '../../types/modal';
import { handleHttpError } from '../../../shared/utils';

const NewCreditHoldModal = ({ open, onClose }: ModalProps) => {
    const [addCreditHold] = useAddCreditHoldMutation();
    const { data: guests } = useGetAllGuestsQuery('/guests');

    const [guestList, setGuestList] = useState<
        { value: string; label: string }[]
    >([]);

    const Schema = z.object({
        guest: z.object({ id: z.number() }),
    });
    type CreditHold = z.infer<typeof Schema>;

    const methods = useForm<CreditHold>({
        resolver: zodResolver(Schema),
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, isSubmitting, errors },
    } = methods;

    const onSubmit = async (data: CreditHold) => {
        try {
            await addCreditHold(data).unwrap();
            handleClose();
            toast.success('Creddit hold placed successfully');
        } catch (error) {
            const errorMessage = handleHttpError(error);
            toast.error(errorMessage);
        }
    };

    const handleClose = () => {
        reset({});
        onClose();
    };

    useEffect(() => {
        if (!guests) return;
        setGuestList(
            guests.map((guest: any) => ({
                value: guest.id,
                label: guest.name,
            })),
        );
    }, [guests]);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='xl'>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle sx={{ alignItems: 'flex-start' }}>
                        <Stack spacing={1} sx={{ minWidth: 0 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    minWidth: 0,
                                }}
                            >
                                New Credit Hold
                            </Box>

                            <Typography sx={{ fontSize: 14 }}>
                                Note: Placing a company on credit hold will
                                prevent them from making further reservations,
                                and notify the primary billing contact of these
                                changes.
                            </Typography>
                        </Stack>
                        <IconButton
                            onClick={handleClose}
                            size='small'
                            aria-label='close'
                            sx={{ color: 'text.primary' }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            width: '100%',
                            minWidth: { xs: '100%', md: '800px' },
                            px: { xs: 1, sm: 3 },
                        }}
                    >
                        <Stack spacing={1}>
                            <Box>
                                <InputLabel required>Guest Company</InputLabel>
                                <FormAutocomplete
                                    name='guest.id'
                                    control={control}
                                    options={guestList}
                                    placeholder='Select company'
                                />
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant='contained'
                            color='secondary'
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                        <CustomButton
                            variant='contained'
                            color='primary'
                            type='submit'
                            disabled={isSubmitting || !isDirty}
                        >
                            Place on Credit Hold
                        </CustomButton>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default NewCreditHoldModal;
